import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Building2, Mail, MessageCircle, Filter, ShieldCheck,
  CheckCircle2, Trash2, FileText, Download, Sparkles, Loader2, UserPlus, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import useConfirmDialog from "@/lib/useConfirmDialog";
import { openWhatsApp } from "@/lib/whatsappTemplates";
import ExpiryBadges from "@/components/ExpiryBadges";

const STATUS_OPTIONS = [
  "pending_docs", "under_review", "contract_sent",
  "onboarding", "active", "archived", "rejected",
];
const STATUS_COLOR = {
  pending_docs:  "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  under_review:  "bg-blue-500/15 text-blue-300 border-blue-500/30",
  contract_sent: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  onboarding:    "bg-purple-500/15 text-purple-300 border-purple-500/30",
  active:        "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  archived:      "bg-zinc-700/40 text-zinc-500 border-zinc-600/40",
  rejected:      "bg-red-500/15 text-red-300 border-red-500/30",
};

const DOC_LABELS = {
  prometna_dozvola:      "Prometna dozvola",
  polica_osiguranja:     "Polica osiguranja",
  taxi_certificate:      "Taxi certifikat",
  yellow_paper:          "Yellow Paper (legacy)",
  drivers_licence:       "Vozačka",
  id_document:           "ID / Putovnica",
  headshot:              "Profilna",
  pension_confirmation:  "Mirovinsko (legacy)",
  criminal_record:       "Nekažnjavanje",
  firm_extract:          "Sudski/Obrtni",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminDriverApplications() {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPath, setFilterPath] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [termsDraft, setTermsDraft] = useState({}); // per-app employment terms editor state
  const [vehicleModalFor, setVehicleModalFor] = useState(null); // {row, preview} | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/driver-applications");
      setRows(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        navigate("/admin");
        return;
      }
      toast.error("Failed to load driver applications.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((r) => (
    (filterStatus === "all" || r.status === filterStatus) &&
    (filterPath === "all" || r.path === filterPath)
  )), [rows, filterStatus, filterPath]);

  const counters = useMemo(() => ({
    total: rows.length,
    pending_docs: rows.filter((r) => r.status === "pending_docs").length,
    under_review: rows.filter((r) => r.status === "under_review").length,
    onboarding:   rows.filter((r) => r.status === "onboarding").length,
    active:       rows.filter((r) => r.status === "active").length,
  }), [rows]);

  const downloadFile = async (appId, slot, filename) => {
    try {
      const resp = await api.get(
        `/admin/driver-applications/${appId}/files/${slot}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      // Open inline in a new tab. For a forced download the caller could
      // switch this to <a download>, but inline preview is better for admins.
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup blocked — fall back to a hidden anchor download.
        const a = document.createElement("a");
        a.href = url; a.download = filename || `${slot}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download file.");
    }
  };

  const [regenBusy, setRegenBusy] = useState(null);
  const [certBusy, setCertBusy] = useState(null);

  const approveCertified = async (r, resend = false) => {
    setCertBusy(r.id);
    try {
      const { data } = await api.post(
        `/admin/driver-applications/${r.id}/approve-certified${resend ? "?resend=true" : ""}`);
      toast.success(
        `Paket dokumenata ${data.email_sent ? "poslan e-mailom" : "— e-mail NIJE poslan"}` +
        (data.vehicle_registered ? " · vozilo upisano u evidenciju" : ""));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odobrenje nije uspjelo");
    } finally { setCertBusy(null); }
  };

  const downloadRegistry = async () => {
    try {
      const { data } = await api.get("/admin/vehicle-registry/download", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "Azurna_evidencija_vozila_JUKE_TRANSFERS.docx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Preuzimanje evidencije nije uspjelo"); }
  };
  const [regBusy, setRegBusy] = useState(false);
  const downloadRegistryPdf = async () => {
    setRegBusy(true);
    try {
      const { data } = await api.get("/admin/vehicle-registry/download.pdf", { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "Azurna_evidencija_vozila_JUKE_TRANSFERS.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF evidencija preuzeta (sinkronizirana s Google Docs).");
    } catch { toast.error("Preuzimanje PDF evidencije nije uspjelo"); }
    finally { setRegBusy(false); }
  };
  const syncRegistry = async () => {
    setRegBusy(true);
    try {
      const { data } = await api.post("/admin/vehicle-registry/sync");
      toast.success(`Sinkronizirano s Google Docs — ${data.doc_rows} vozila (+${data.added} / ~${data.updated} / −${data.deleted}).`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sinkronizacija nije uspjela"); }
    finally { setRegBusy(false); }
  };
  const [driverBusy, setDriverBusy] = useState(null);
  const createDriver = async (r) => {
    setDriverBusy(r.id);
    try {
      const { data } = await api.post(`/admin/driver-applications/${r.id}/create-driver`);
      toast.success(data.created
        ? `Driver profile created${data.email_sent ? " — activation email sent" : ""}.`
        : "Driver already exists — new activation link sent.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Driver creation failed.");
    } finally {
      setDriverBusy(null);
    }
  };
  const regenerateContracts = async (id) => {
    setRegenBusy(id);
    try {
      const { data } = await api.post(`/admin/driver-applications/${id}/regenerate-contracts`);
      toast.success(`Contracts regenerated: ${data.regenerated.join(", ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regeneration failed.");
    } finally {
      setRegenBusy(null);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/driver-applications/${id}`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success(`Status → ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (id) => {
    const notes = notesDraft[id] ?? "";
    try {
      const { data } = await api.patch(`/admin/driver-applications/${id}`, { admin_notes: notes });
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const saveEmploymentTerms = async (id) => {
    const t = termsDraft[id] ?? {};
    const payload = {
      gross_salary_eur: t.gross_salary_eur === "" ? null : Number(t.gross_salary_eur || 0),
      hours_per_week:   t.hours_per_week === "" ? null : Number(t.hours_per_week || 0),
      employment_start_date: t.employment_start_date || "",
      lease_amount_eur: t.lease_amount_eur === "" ? null : Number(t.lease_amount_eur || 0),
      lease_start_date: t.lease_start_date || "",
    };
    try {
      const { data } = await api.patch(
        `/admin/driver-applications/${id}`,
        { employment_terms: payload },
      );
      setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
      toast.success("Employment terms saved · will be injected on Approve");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const approve = async (row) => {
    // Feb-2026: certain (path × has_own_vehicle) combos need a vehicle
    // assignment (weekly rate + VIN/plates/color) before contracts can
    // render. Check with the backend first; if blocked, open modal.
    setBusyId(row.id);
    try {
      const { data: preview } = await api.get(
        `/admin/driver-applications/${row.id}/contracts-preview`);
      if (preview.needs_vehicle_assignment
          && preview.missing_assignment_fields?.length > 0) {
        setVehicleModalFor({ row, preview });
        setBusyId(null);
        return;
      }
      const contractLabels = preview.contracts.map((c) => c.label).join(" + ");
      const ok = await confirm({
        title: "Approve application?",
        description: `This will provision a temporary password for ${row.first_name} ${row.last_name}, generate ${contractLabels}, and email them the onboarding credentials.`,
        confirmLabel: "Approve & send credentials",
        destructive: false,
      });
      if (!ok) { setBusyId(null); return; }
      const { data } = await api.post(`/admin/driver-applications/${row.id}/approve`);
      toast.success(`Credentials sent · temp password: ${data.temp_password}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const submitVehicleAssignment = async ({ row }, payload) => {
    setBusyId(row.id);
    try {
      await api.post(
        `/admin/driver-applications/${row.id}/vehicle-assignment`, payload);
      toast.success("Vehicle assignment saved");
      setVehicleModalFor(null);
      // Now retry approve automatically
      const { data } = await api.post(`/admin/driver-applications/${row.id}/approve`);
      toast.success(`Credentials sent · temp password: ${data.temp_password}`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Vehicle assignment failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: "Delete application?",
      description: `This permanently removes the application record for ${row.first_name} ${row.last_name}. Uploaded documents in storage will remain but be orphaned.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/driver-applications/${row.id}`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      toast.success("Application deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <ConfirmDialog />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link
              to="/admin/dashboard"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#C5A059] transition-colors mb-2"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to dashboard
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-light">
              Driver Applications
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              Fleet & B2B partner onboarding queue
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start">
            <button
              type="button"
              data-testid="sync-vehicle-registry"
              onClick={syncRegistry}
              disabled={regBusy}
              className="h-9 px-4 rounded-full border border-white/20 text-zinc-300 text-[10px] uppercase tracking-[0.2em] hover:bg-white/10 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${regBusy ? "animate-spin" : ""}`} /> Sync Google Doc
            </button>
            <button
              type="button"
              data-testid="download-vehicle-registry-pdf"
              onClick={downloadRegistryPdf}
              disabled={regBusy}
              className="h-9 px-4 rounded-full bg-[#C5A059] text-black text-[10px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] transition-colors inline-flex items-center gap-2 disabled:opacity-50 font-semibold"
            >
              <Download className="w-3.5 h-3.5" /> Evidencija (PDF)
            </button>
            <button
              type="button"
              data-testid="download-vehicle-registry"
              onClick={downloadRegistry}
              className="h-9 px-4 rounded-full border border-[#C5A059]/50 text-[#C5A059] text-[10px] uppercase tracking-[0.2em] hover:bg-[#C5A059]/10 transition-colors inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" /> DOCX
            </button>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
          <Counter label="Total" value={counters.total} tint="zinc" />
          <Counter label="Pending Docs" value={counters.pending_docs} tint="zinc" />
          <Counter label="Under Review" value={counters.under_review} tint="blue" />
          <Counter label="Onboarding" value={counters.onboarding} tint="purple" />
          <Counter label="Active" value={counters.active} tint="emerald" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <Filter className="w-3.5 h-3.5 text-zinc-500" />
          <FilterPill
            testId="filter-status-all"
            active={filterStatus === "all"}
            onClick={() => setFilterStatus("all")}
          >All statuses</FilterPill>
          {STATUS_OPTIONS.map((s) => (
            <FilterPill
              key={s}
              testId={`filter-status-${s}`}
              active={filterStatus === s}
              onClick={() => setFilterStatus(s)}
            >{s.replace(/_/g, " ")}</FilterPill>
          ))}
          <span className="w-px h-4 bg-white/10 mx-1" />
          <FilterPill
            testId="filter-path-all"
            active={filterPath === "all"}
            onClick={() => setFilterPath("all")}
          >Both paths</FilterPill>
          <FilterPill
            testId="filter-path-A"
            active={filterPath === "A"}
            onClick={() => setFilterPath("A")}
          >Fleet (A)</FilterPill>
          <FilterPill
            testId="filter-path-B"
            active={filterPath === "B"}
            onClick={() => setFilterPath("B")}
          >Partner (B)</FilterPill>
        </div>

        {/* Rows */}
        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-zinc-600 text-sm">
            No applications match this filter.
          </div>
        ) : (
          <div className="space-y-2" data-testid="driver-apps-list">
            {filtered.map((r) => {
              const isOpen = expandedId === r.id;
              const filesBySlot = Object.fromEntries((r.files || []).map((f) => [f.slot, f]));
              const requiredSlots = Object.entries(DOC_LABELS)
                .filter(([k]) => {
                  // Legacy slots: only show if the app actually had them uploaded
                  if (k === "pension_confirmation" || k === "yellow_paper") return !!filesBySlot[k];
                  // firm_extract is Path-B only
                  if (k === "firm_extract") return r.path === "B";
                  // Vehicle-registration docs are skipped when no own vehicle
                  const vehicleDocs = ["prometna_dozvola", "polica_osiguranja"];
                  if (vehicleDocs.includes(k)) return r.has_own_vehicle !== false;
                  return true;
                });
              const complete = requiredSlots.every(([k]) => filesBySlot[k]);
              return (
                <div
                  key={r.id}
                  data-testid={`app-row-${r.id}`}
                  className="rounded-2xl bg-black/30 border border-white/5 overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      r.path === "A" ? "bg-blue-500/15 text-blue-300" : "bg-[#C5A059]/15 text-[#C5A059]"
                    }`}>
                      {r.path === "A" ? <User className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {r.first_name} {r.last_name}
                        {r.company_name && <span className="text-zinc-500"> · {r.company_name}</span>}
                      </p>
                      <p className="text-[11px] text-zinc-500 truncate">
                        {r.email} · {r.vehicle_make} {r.vehicle_model} · {r.vehicle_plates}
                      </p>
                      <ExpiryBadges rec={r} />
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${STATUS_COLOR[r.status] || STATUS_COLOR.pending_docs}`}>
                      {r.status.replace(/_/g, " ")}
                    </span>
                    <span
                      title={`${(r.files || []).length} / ${requiredSlots.length} docs`}
                      className={`text-[10px] uppercase tracking-widest ${complete ? "text-emerald-400" : "text-zinc-500"}`}
                    >
                      {(r.files || []).length}/{requiredSlots.length}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-4">
                      {/* Meta grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <MetaRow label="OIB">{r.oib}</MetaRow>
                        <MetaRow label="Phone">{r.phone}</MetaRow>
                        <MetaRow label="Address">{r.address || "—"}, {r.city || "—"}</MetaRow>
                        <MetaRow label="IBAN">{r.iban || "—"}</MetaRow>
                        {r.path === "A" && (
                          <>
                            <MetaRow label="Employment">{r.employment_type || "—"}</MetaRow>
                            <MetaRow label="Experience">{r.driving_experience_years ?? "—"} yrs</MetaRow>
                          </>
                        )}
                        {r.path === "B" && (
                          <>
                            <MetaRow label="Company OIB">{r.company_oib || "—"}</MetaRow>
                            <MetaRow label="Company IBAN">{r.company_iban || "—"}</MetaRow>
                          </>
                        )}
                        <MetaRow label="Taxi licence">{r.taxi_license_status}</MetaRow>
                        <MetaRow label="Vehicle">{r.vehicle_make} {r.vehicle_model} · {r.vehicle_color} · {r.vehicle_year}</MetaRow>
                        <MetaRow label="VIN">{r.vehicle_vin}</MetaRow>
                        <MetaRow label="Plates">{r.vehicle_plates}</MetaRow>
                        <MetaRow label="Applied">{fmtDate(r.created_at)}</MetaRow>
                        {r.credentials_sent_at && (
                          <MetaRow label="Credentials sent">{fmtDate(r.credentials_sent_at)}</MetaRow>
                        )}
                      </div>

                      {/* Documents */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Documents</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {requiredSlots.map(([slot, label]) => {
                            const f = filesBySlot[slot];
                            return (
                              <button
                                type="button"
                                key={slot}
                                data-testid={`doc-${slot}-${r.id}`}
                                disabled={!f}
                                onClick={() => f && downloadFile(r.id, slot, f.original_filename)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] transition-colors text-left ${
                                  f ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 cursor-pointer"
                                    : "border-zinc-700/40 bg-zinc-800/20 text-zinc-500 cursor-not-allowed"
                                }`}
                                title={f ? f.original_filename : "Not uploaded yet"}
                              >
                                {f ? <Download className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                <span className="truncate">{label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Auto-generated contract PDFs (populated on Approve) */}
                      {(r.files || []).some((f) => f.slot?.startsWith("contract_")) && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
                            Contracts <span className="text-zinc-600 normal-case tracking-normal">· auto-generated on Approve</span>
                            <button
                              type="button"
                              data-testid={`regenerate-contracts-${r.id}`}
                              onClick={() => regenerateContracts(r.id)}
                              disabled={regenBusy === r.id}
                              className="ml-auto normal-case tracking-normal px-2.5 py-1 rounded-full border border-white/10 text-zinc-400 hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-50"
                            >
                              {regenBusy === r.id ? "Regenerating…" : "Regenerate"}
                            </button>
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {(r.files || [])
                              .filter((f) => f.slot?.startsWith("contract_"))
                              .map((f) => (
                                <button
                                  key={f.slot}
                                  type="button"
                                  data-testid={`doc-${f.slot}-${r.id}`}
                                  onClick={() => downloadFile(r.id, f.slot, f.original_filename)}
                                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/5 text-[#C5A059] text-[11px] hover:bg-[#C5A059]/10 transition-colors text-left"
                                >
                                  <Download className="w-3 h-3" />
                                  <span className="truncate">{f.label || f.slot.replace("contract_", "")}</span>
                                </button>
                              ))}
                          </div>
                          {r.contract_signatures && Object.keys(r.contract_signatures).length > 0 && (
                            <div className="mt-2 space-y-0.5" data-testid={`signatures-${r.id}`}>
                              {Object.entries(r.contract_signatures).map(([slot, sig]) => (
                                <p key={slot} className="text-[10px] text-emerald-400/80">
                                  ✓ {slot.replace("contract_", "")} potpisan — {sig.full_name} · {sig.signed_at ? new Date(sig.signed_at).toLocaleString("hr-HR") : ""}
                                </p>
                              ))}
                            </div>
                          )}

                          {/* Certified copy (Porezna) → compliance package */}
                          {(r.files || []).some((f) => f.slot === "contract_certified") && (
                            <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3" data-testid={`certified-block-${r.id}`}>
                              <p className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
                                Ovjereni ugovor o zakupu (Porezna uprava) učitan
                              </p>
                              {r.certified_approved_at ? (
                                <div className="flex flex-wrap items-center gap-3">
                                  <p className="text-[11px] text-emerald-400" data-testid={`certified-approved-${r.id}`}>
                                    ✓ Odobreno · paket dokumenata poslan {new Date(r.certified_approved_at).toLocaleString("hr-HR")}
                                  </p>
                                  <button type="button" data-testid={`resend-package-${r.id}`}
                                    onClick={() => approveCertified(r, true)} disabled={certBusy === r.id}
                                    className="px-3 py-1 rounded-full border border-white/10 text-zinc-400 hover:border-[#C5A059] hover:text-[#C5A059] text-[10px] uppercase tracking-widest disabled:opacity-50">
                                    {certBusy === r.id ? "Šaljem…" : "Pošalji ponovno"}
                                  </button>
                                </div>
                              ) : (
                                <button type="button" data-testid={`approve-certified-${r.id}`}
                                  onClick={() => approveCertified(r)} disabled={certBusy === r.id}
                                  className="h-9 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] uppercase tracking-[0.2em] font-semibold disabled:opacity-50">
                                  {certBusy === r.id ? "Šaljem…" : "Odobri ovjeru & pošalji paket dokumenata"}
                                </button>
                              )}
                              <p className="text-[10px] text-zinc-500 mt-2">
                                Šalje: Interni akt · Blagajnički maksimum (2.000 €) · Dozvola Grada Splita · Licencija — i upisuje vozilo u ažurnu evidenciju.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Employment terms — Path A only */}
                      {r.path === "A" && (() => {
                        const terms = termsDraft[r.id] ?? r.employment_terms ?? {};
                        const setField = (k, v) => setTermsDraft((td) => ({
                          ...td, [r.id]: { ...(td[r.id] ?? r.employment_terms ?? {}), [k]: v },
                        }));
                        return (
                          <div data-testid={`employment-terms-${r.id}`}>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                              Employment terms
                              <span className="text-zinc-600 normal-case tracking-normal ml-2">
                                · injected into Ugovor o radu + Ugovor o najmu on Approve
                              </span>
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              <TermsField label="Gross salary (€)" testId={`gross-${r.id}`}
                                type="number" value={terms.gross_salary_eur ?? ""}
                                onChange={(v) => setField("gross_salary_eur", v)} />
                              <TermsField label="Hours / week" testId={`hours-${r.id}`}
                                type="number" value={terms.hours_per_week ?? ""}
                                onChange={(v) => setField("hours_per_week", v)} />
                              <TermsField label="Start date (dd.mm.yyyy.)" testId={`start-${r.id}`}
                                value={terms.employment_start_date ?? ""}
                                onChange={(v) => setField("employment_start_date", v)} />
                              <TermsField label="Lease amount (€/mo)" testId={`lease-amt-${r.id}`}
                                type="number" value={terms.lease_amount_eur ?? ""}
                                onChange={(v) => setField("lease_amount_eur", v)} />
                              <TermsField label="Lease start (dd.mm.yyyy.)" testId={`lease-start-${r.id}`}
                                value={terms.lease_start_date ?? ""}
                                onChange={(v) => setField("lease_start_date", v)} />
                            </div>
                            <button
                              data-testid={`save-terms-${r.id}`}
                              onClick={() => saveEmploymentTerms(r.id)}
                              className="mt-2 h-8 px-3 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 rounded-lg"
                            >Save employment terms</button>
                          </div>
                        );
                      })()}

                      {/* Admin notes */}
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Admin notes</p>
                        <Textarea
                          data-testid={`notes-${r.id}`}
                          value={notesDraft[r.id] ?? r.admin_notes ?? ""}
                          onChange={(e) => setNotesDraft((n) => ({ ...n, [r.id]: e.target.value }))}
                          rows={3}
                          placeholder="Interview notes, red flags, follow-ups…"
                          className="w-full text-sm bg-black/40 border-white/10 text-zinc-100 resize-none"
                        />
                        <button
                          onClick={() => saveNotes(r.id)}
                          className="mt-2 h-8 px-3 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 rounded-lg"
                        >Save notes</button>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
                        <select
                          data-testid={`status-select-${r.id}`}
                          value={r.status}
                          onChange={(e) => updateStatus(r.id, e.target.value)}
                          className="h-9 text-[11px] uppercase tracking-widest bg-black/40 border border-white/10 rounded-xl px-3 focus:outline-none text-zinc-200"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                          ))}
                        </select>
                        {["onboarding", "active"].includes(r.status) && (
                          <button
                            data-testid={`create-driver-${r.id}`}
                            onClick={() => createDriver(r)}
                            disabled={driverBusy === r.id}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/10 hover:bg-[#C5A059]/20 disabled:opacity-60 text-[#C5A059] text-[11px] uppercase tracking-widest font-semibold"
                          >
                            {driverBusy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                            {r.driver_id ? "Resend activation" : "Create driver"}
                          </button>
                        )}
                        {!r.credentials_sent_at && (
                          <button
                            data-testid={`approve-${r.id}`}
                            onClick={() => approve(r)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black text-[11px] uppercase tracking-widest font-semibold"
                          >
                            {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Approve & send credentials
                          </button>
                        )}
                        {r.credentials_sent_at && (
                          <span
                            data-testid={`credentials-sent-${r.id}`}
                            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-widest"
                          >
                            <ShieldCheck className="w-3 h-3" />
                            Credentials sent · {fmtDate(r.credentials_sent_at)}
                          </span>
                        )}
                        <a
                          href={`mailto:${r.email}`}
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-white/10 hover:border-[#C5A059]/40 hover:text-[#C5A059] text-zinc-400 text-[10px] uppercase tracking-widest"
                        >
                          <Mail className="w-3 h-3" />Email
                        </a>
                        <button
                          onClick={() => openWhatsApp(r.phone, `Hi ${r.first_name}, this is Juke Transfers regarding your driver application.`)}
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-200 text-[10px] uppercase tracking-widest"
                        >
                          <MessageCircle className="w-3 h-3" />WhatsApp
                        </button>
                        <div className="flex-1" />
                        <button
                          onClick={() => remove(r)}
                          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 text-red-300 text-[10px] uppercase tracking-widest"
                        >
                          <Trash2 className="w-3 h-3" />Delete
                        </button>
                      </div>

                      {r.history && r.history.length > 0 && (
                        <div className="pt-3 border-t border-white/5">
                          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 inline-flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" />
                            History
                          </p>
                          <ol className="space-y-1 text-[11px] text-zinc-500 font-mono">
                            {r.history.slice(-8).reverse().map((h, i) => (
                              <li key={i}>
                                <span className="text-zinc-600">{(h.at || "").slice(0, 16)}</span>
                                <span className="text-zinc-300 ml-2">{h.event}</span>
                                {h.actor && <span className="text-zinc-600"> · {h.actor}</span>}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {vehicleModalFor && (
        <VehicleAssignmentModal
          ctx={vehicleModalFor}
          busy={busyId === vehicleModalFor.row.id}
          onClose={() => setVehicleModalFor(null)}
          onSubmit={(payload) => submitVehicleAssignment(vehicleModalFor, payload)}
        />
      )}
    </div>
  );
}

function VehicleAssignmentModal({ ctx, busy, onClose, onSubmit }) {
  const { row, preview } = ctx;
  const isB2B = preview.path === "B";
  const [form, setForm] = useState({
    make: "", model: "", color: "", plates: "", vin: "", year: "",
    weekly_rate_eur: "", lease_start_date: "",
  });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      make: form.make.trim(), model: form.model.trim(), color: form.color.trim(),
      plates: form.plates.trim(), vin: form.vin.trim(),
    };
    if (form.year) payload.year = Number(form.year);
    if (form.weekly_rate_eur) payload.weekly_rate_eur = Number(form.weekly_rate_eur);
    if (form.lease_start_date) payload.lease_start_date = form.lease_start_date;
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="vehicle-assignment-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-[#0d0d0d] border border-[#C5A059]/40 rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 space-y-4">
        <div>
          <p className="font-display text-xl text-white">Assign vehicle · Generate {isB2B ? "Ugovor o zakupu V2" : "Zapisnik o zaduženju"}</p>
          <p className="text-xs text-zinc-500 mt-1">
            {row.first_name} {row.last_name}
            {isB2B ? " · B2B partner" : " · Fleet driver"} · company vehicle
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Make *</span>
            <input required data-testid="va-make" value={form.make} onChange={upd("make")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
          <label className="col-span-1">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Model *</span>
            <input required data-testid="va-model" value={form.model} onChange={upd("model")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Color *</span>
            <input required data-testid="va-color" value={form.color} onChange={upd("color")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Plates *</span>
            <input required data-testid="va-plates" value={form.plates} onChange={upd("plates")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
          <label className="col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">VIN * (11–20 chars)</span>
            <input required minLength={11} maxLength={20} data-testid="va-vin"
                    value={form.vin} onChange={upd("vin")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white font-mono" />
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Year</span>
            <input type="number" min={1990} max={2035} data-testid="va-year"
                    value={form.year} onChange={upd("year")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
          {isB2B && (
            <label>
              <span className="text-[10px] uppercase tracking-widest text-[#C5A059]">Weekly rate (€) *</span>
              <input required type="number" min={0} step={5} data-testid="va-weekly-rate"
                      value={form.weekly_rate_eur} onChange={upd("weekly_rate_eur")}
                      className="mt-1 w-full h-10 bg-[#C5A059]/[0.05] border border-[#C5A059]/40 rounded-lg px-3 text-sm text-white font-mono" />
            </label>
          )}
          <label className={isB2B ? "" : "col-span-2"}>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Lease start</span>
            <input type="date" data-testid="va-start" value={form.lease_start_date}
                    onChange={upd("lease_start_date")}
                    className="mt-1 w-full h-10 bg-zinc-900 border border-white/10 rounded-lg px-3 text-sm text-white" />
          </label>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/40 p-3 text-[11px] text-zinc-400 leading-relaxed">
          On submit, we save the assignment, generate {preview.contracts.map((c) => c.label).join(" + ")}, and email temp credentials to {row.email}.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
                    className="h-10 px-4 rounded-full border border-white/15 text-zinc-300 text-[10px] uppercase tracking-widest disabled:opacity-40">
            Cancel
          </button>
          <button type="submit" disabled={busy} data-testid="va-submit"
                    className="h-10 px-5 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold text-[10px] uppercase tracking-widest disabled:opacity-40">
            Assign &amp; Generate
          </button>
        </div>
      </form>
    </div>
  );
}

function Counter({ label, value, tint }) {
  const tintClass = tint === "emerald" ? "text-emerald-300 border-emerald-500/30"
                  : tint === "blue"    ? "text-blue-300 border-blue-500/30"
                  : tint === "purple"  ? "text-purple-300 border-purple-500/30"
                  : "text-zinc-300 border-zinc-500/30";
  return (
    <div className={`rounded-xl bg-black/30 border ${tintClass} px-4 py-3`}>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-2xl font-display mt-1">{value}</p>
    </div>
  );
}

function FilterPill({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`h-7 px-3 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
        active
          ? "bg-[#C5A059] border-[#C5A059] text-black"
          : "border-white/10 text-zinc-500 hover:text-white hover:border-[#C5A059]/40"
      }`}
    >{children}</button>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-zinc-600">{label}</span>
      <span className="text-zinc-200 truncate">{children}</span>
    </div>
  );
}

function TermsField({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-zinc-600 mb-1 block">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-xs text-white focus:outline-none focus:border-[#C5A059]/50"
      />
    </label>
  );
}
